import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { ReactSortable, Sortable } from 'react-sortablejs';
import { AddIcon } from 'tdesign-icons-react';
import { Button, Collapse, DialogProps, Form, Input, InputValue, MessagePlugin, Switch } from 'tdesign-react';

import { IFormItemProps } from '@/components';
import { EditorDel, EditorDrag } from '@/components/icon';
import { Branch, IScene } from '@/interface';
import { validateNumActiveCond } from '@/pages/mission/info/validate';
import { StoryNumberListData } from '@/pages/numberSystem/setNumberTable/table';
import arrayReducer, { reducerActions } from '@/reducer/arrayReducers';

import styles from './index.module.less';
import MultiCardContainer from './numberSystem';
import { MultiCardContainerDesc } from './numberSystem/desc';

const { FormItem } = Form;
const { Panel } = Collapse;

export const defaultValue = { optionName: '', missionId: '' };

export const BranchArray: React.FC<
{
  value: Branch[];
  disabled?: boolean;
  modal?: {
    text: string;
    fieldsConfig?: IFormItemProps[];
    modalConfig?: DialogProps;
    modalTitle?: { title: string; subTitle: string };
  };
  options: { label: string, value: string }[];
  passConditions: IScene['passConditions'];
  onChange: (value: Branch[]) => void;
  storyNumber?: {
    storyNumberList: StoryNumberListData[];
    storyNumberLoading: boolean;
  }
}
> = (props) => {
  const {
    value = [],
    onChange,
    disabled = false,
    passConditions = [],
    storyNumber = { storyNumberList: [], storyNumberLoading: false },
  } = props;

  const [arrayReducerData, dispatch] = useReducer(arrayReducer, { items: [] });
  // const [compValue, setCompValue] = useState<Branch[]>([]);

  const compValue = useMemo(() => arrayReducerData.items || [], [arrayReducerData.changeNumber]);

  useEffect(() => {
    if (arrayReducerData?.changeNumber) {
      onChange(arrayReducerData?.items);
    }
  }, [arrayReducerData?.changeNumber]);

  useEffect(() => {
    dispatch({
      type: reducerActions.INIT_EMPTY,
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(value)) {
      if (arrayReducerData?.items.length === 0) {
        dispatch({
          type: reducerActions.CHANGE_ALL,
          payload: {
            value: [...value],
          },
        });
      }
      // setCompValue(setKeyIndex([...value]));
    } else {
      // setCompValue([]);
    }
  }, [value]);

  const changeArrayInfoObject = useCallback(
    (key: number, value: { [key: string]: unknown }) => {
      dispatch({
        type: reducerActions.UPDATE_ITEM,
        payload: {
          index: key,
          value,
        },
      });
    },
    [compValue],
  );

  // 断是否存在关联剧情
  const hasAssociatedMission = (val: Branch | undefined, passConditions: IScene['passConditions']) => {
    if (!val || isEmpty(passConditions)) return false;
    const { missionId } = val;
    return passConditions?.some((item) => {
      const missionList = item?.specifyMission?.missions || [];
      return missionList.some((mis: { missionId?: string }) => mis.missionId === missionId && item?.triggerMissionId);
    });
  };

  const removeItem = (
    index: number,
    event?: React.MouseEvent<HTMLElement, MouseEvent>,
    val?: Branch,
  ) => {
    event?.stopPropagation();

    // 删除之前如果有剧情，则提示需要先删除剧情
    if (hasAssociatedMission(val, passConditions)) {
      MessagePlugin.error('请先删除该选项条件关联的剧情');
      return;
    }

    dispatch({
      type: reducerActions.REMOVE_ITEM,
      payload: {
        index,
      },
    });
  };

  const addItem = useCallback((
    index: number,
    val?: Branch,
    event?: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    event?.stopPropagation();
    const result = val ? val : defaultValue;
    dispatch({
      type: reducerActions.ADD_ITEM,
      payload: {
        index: index + 1,
        value: {
          ...result,
          keyIndex: undefined,
        },
      },
    });
  }, [arrayReducerData]);

  const onEnd = (evt: Sortable.SortableEvent) => {
    const oldIndex = evt.oldIndex || 0;
    const newIndex = evt.newIndex || 0;
    sortData(oldIndex, newIndex);
  };

  const sortData = (oldIndex: number, newIndex: number) => {
    const copyValue = [...compValue];
    const copyValue2 = [...compValue];
    copyValue.splice(oldIndex, 1);
    copyValue.splice(newIndex, 0, copyValue2[oldIndex]);

    dispatch({
      type: reducerActions.CHANGE_ALL,
      payload: {
        value: copyValue,
      },
    });
  };

  const handleChange = useCallback(
    (
      value: { [key: string]: any },
      index: number,
    ) => {
      changeArrayInfoObject(index, value);
    },
    [arrayReducerData],
  );

  const handleKeyup = useCallback((
    _: InputValue,
    ctx: {
      e: React.KeyboardEvent<HTMLDivElement>;
    }, index: number,
  ) => {
    if (ctx.e.code === 'Enter' && ctx.e.altKey === false) {
      addItem(index);
      setTimeout(() => {
        const elements = document.getElementsByClassName(`branch-input-${index + 1}`);
        if (elements?.[0]) {
          elements?.[0]?.querySelector('input')?.focus();
        }
      }, 400);
    }
  }, [compValue]);

  const handleKeydown = useCallback((_: InputValue, ctx: {
    e: React.KeyboardEvent<HTMLDivElement>;
  }) => {
    if (ctx.e.code === 'Enter' && ctx.e.altKey === false) {
      ctx.e.preventDefault();
    }
  }, []);

  return (
    <div className={styles.arrayWrap}>
      <div className={styles.contentEvent}>
        <>
          <ReactSortable
            disabled={disabled}
            handle={'.draggable'}
            list={compValue.map((x: any) => ({ ...x }))}
            onEnd={e => onEnd(e)}
            setList={() => { }}
          >
            {compValue?.length > 0 ? (
              compValue?.map((val, index) => {
                const delDisabled = disabled || compValue?.length <= 1;
                const error = !val?.optionName?.trim();

                const actionCondErrorMsg = val?.useOptionCond
                  ? (validateNumActiveCond(val?.optionCond?.activationCond, '激活选项条件', true) as { message: string })?.message
                  : '';
                const numChangeErrorMsg = val?.useOptionCond
                  ? (validateNumActiveCond(val?.optionCond?.numChange, '选后数值变化') as { message: string })?.message
                  : '';

                return (
                  <div key={val?.keyIndex} className={styles.item}>
                    <div>
                      <FormItem
                        className={classNames(
                          'custom-form-group',
                          styles.group,
                          { [styles.itemMargin]: !error },
                        )
                        }
                      >
                        <div className={styles.drag}>
                          {!disabled && (
                            <div className={classNames('draggable', styles.drag)}>
                              <EditorDrag />
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <Collapse
                            expandIconPlacement={'right'}
                            style={{ flex: 1 }}
                            className={styles.branchCollapse}
                            onChange={() => {
                              handleChange({
                                ...val,
                                keyIndex: val?.keyIndex,
                                openCollapse: !val?.openCollapse,
                              }, index);
                            }}
                          >
                            <Panel header={
                              <div style={{ flex: 1 }}>
                                <div className={styles.contentWrapRow} onClick={e => e.stopPropagation()}>
                                  <div style={{ flex: 1 }}>
                                    <FormItem
                                      label={''}
                                      className={styles.item}
                                      initialData={val?.optionName}
                                    >
                                      <Input
                                        className={`branch-input-${index}`}
                                        disabled={disabled}
                                        onKeyup={(v, ctx) => handleKeyup(v, ctx, index)}
                                        onKeydown={handleKeydown}
                                        placeholder={'请输入'}
                                        maxlength={20}
                                        showLimitNumber
                                        onChange={
                                          (optionName: string) => handleChange({
                                            ...val,
                                            keyIndex: val?.keyIndex,
                                            optionName,
                                          }, index)
                                        }
                                      />
                                    </FormItem>
                                    <div>
                                      {error ? (
                                        <span
                                          className={classNames(styles.errors, 'nova-text-error')}
                                        >
                                          请填写选项标题
                                        </span>
                                      )
                                        : null
                                      }
                                    </div>
                                  </div>
                                  <div className={styles.openCond}>
                                    <span className={styles.label}>选项数值</span>
                                    <Switch size="medium"
                                      value={val?.useOptionCond as any || false} onChange={
                                        (check: boolean) => handleChange({
                                          ...val,
                                          keyIndex: val?.keyIndex,
                                          useOptionCond: check,
                                        }, index)
                                      } />
                                  </div>
                                </div>
                                <MultiCardContainerDesc
                                  val={val}
                                  actionCondErrorMsg={actionCondErrorMsg}
                                  numChangeErrorMsg={numChangeErrorMsg}
                                  storyNumber={storyNumber}
                                />
                              </div>
                            } value="default" className={styles.panel}>
                              <MultiCardContainer
                                value={val?.optionCond || {
                                  activationCond: [],
                                  numChange: [],
                                }}
                                actionCondErrorMsg={actionCondErrorMsg}
                                numChangeErrorMsg={numChangeErrorMsg}
                                disabled={disabled || (val?.useOptionCond !== true)}
                                onChange={(cond) => {
                                  handleChange({
                                    ...val,
                                    keyIndex: val?.keyIndex,
                                    optionName: val?.optionName,
                                    optionCond: cond,
                                  }, index);
                                }} />
                            </Panel>
                          </Collapse>
                        </div>
                        <div className={styles.options}>
                          <Button
                            className={classNames(styles.add, {
                              'nova-disabled-opacity': disabled,
                            })}
                            theme="default"
                            onClick={event => addItem(index, defaultValue, event)}
                            disabled={disabled}
                          >
                            <AddIcon />
                          </Button>
                          <Button
                            className={classNames(styles.delete, {
                              'nova-disabled-opacity': delDisabled,
                            })}
                            disabled={delDisabled}
                            theme="default"
                            onClick={event => removeItem(index, event, val)}
                          >
                            <EditorDel />
                          </Button>
                        </div>
                      </FormItem>
                    </div>
                  </div>
                );
              }))
              : null
            }
          </ReactSortable>
        </>
      </div>
    </div>
  );
};
